import {
  UpsertProjectFormEntity,
  UpsertProjectRequest,
  ProjectDetailEntity,
  ProjectDetailResponse,
  ProjectEntity,
  ProjectsResponse,
  ProjectAssignUserEntity,
} from 'src/types'

// UpsertProjectRequest['compassUsers'] はundefined も取り得るため、その場合はneverを返す
type CompassUser = UpsertProjectRequest['compassUsers'] extends (infer U)[] ? U : never

export const ProjectTranslator = {
  toUpsertRequest: (formEntity: UpsertProjectFormEntity): UpsertProjectRequest => {
    const projectStatusName = formEntity.statusName
    if (!projectStatusName) {
      throw new Error('projectStatusName is required')
    }

    const internalUserRequests = formEntity.projectUsers.map(user => ({
      userId: user.id,
      compassRoleId: user.compassRoleId,
      isExternal: false,
    }))
    const externalUserRequests = formEntity.externalEditors.map(user => ({
      userId: user.id,
      compassRoleId: user.compassRoleId,
      isExternal: true,
    }))

    // リクエストとして送る際は社内/社外をまとめて送信する
    const userRequests = [...internalUserRequests, ...externalUserRequests]
    // UI側で弾いているため、compassRoleIdが存在しない場合はエラーを投げ、リクエストとして正しい型に変換する
    const compassUsers: CompassUser[] = userRequests.filter((user): user is CompassUser => {
      if (!user.compassRoleId) {
        throw new Error('compassRoleId is required')
      }
      return true
    })

    return {
      title: formEntity.name,
      code: formEntity.code,
      email: formEntity.email && formEntity.email !== '' ? formEntity.email : null,
      emailDisplayName:
        formEntity.emailDisplayName && formEntity.emailDisplayName !== ''
          ? formEntity.emailDisplayName
          : null,
      projectStatusName,
      zipcode: formEntity.zipcode,
      prefecture: formEntity.prefecture,
      city: formEntity.city,
      address: formEntity.address,
      structureCount: formEntity.structureCount,
      roomCount: formEntity.roomCount,
      planCount: formEntity.planCount,
      averagePriceRange: formEntity.averagePriceRange && formEntity.averagePriceRange * 10_000,
      compassUsers,
      sendAssignEmail: formEntity.isSendAssignEmail,
    }
  },

  toFormEntity: (entity: ProjectDetailEntity): UpsertProjectFormEntity => {
    return {
      ...entity,
      code: entity.code ?? undefined,
      statusName: entity.status.name,
      zipcode: entity.zipcode ?? undefined,
      email: entity.email,
      city: entity.city ?? undefined,
      address: entity.address ?? undefined,
      averagePriceRange: entity.averagePriceRange ? entity.averagePriceRange / 10_000 : undefined,
      projectUsers: entity.internalUsers.map(internalUser => ({
        id: internalUser.id,
        email: internalUser.email,
        name: internalUser.name,
        compassRoleId: internalUser.compassRole.id,
        compassRoleName: internalUser.compassRole.name,
        compassRolePriority: internalUser.compassRole.priority,
      })),
      externalEditors: entity.externalUsers.map(externalUser => ({
        id: externalUser.id,
        email: externalUser.email,
        name: externalUser.name,
        compassRoleId: externalUser.compassRole.id,
        compassRoleName: externalUser.compassRole.name,
        compassRolePriority: externalUser.compassRole.priority,
      })),
      isSendAssignEmail: false,
    }
  },

  toDetailEntity: (response: ProjectDetailResponse): ProjectDetailEntity => {
    const internalUsers: ProjectAssignUserEntity[] =
      response.users
        ?.filter(user => !user.isExternal)
        .map(user => ({
          ...user,
          compassRole: user.role,
          assignedAt: new Date(user.assignedAt),
        })) ?? []
    const externalUsers: ProjectAssignUserEntity[] =
      response.users
        ?.filter(user => user.isExternal)
        .map(user => ({
          ...user,
          compassRole: user.role,
          assignedAt: new Date(user.assignedAt),
        })) ?? []
    return {
      ...response,
      status: {
        ...response.status,
        priority: response.status.priority ?? 0,
      },
      email: response.email ?? undefined,
      emailDisplayName: response.emailDisplayName ?? undefined,
      name: response.title,
      structureCount: response.structureCount ?? undefined,
      roomCount: response.roomCount ?? undefined,
      planCount: response.planCount ?? undefined,
      averagePriceRange: response.averagePriceRange ?? undefined,
      purchaserCount: response.purchaserCount ?? undefined,
      applicationCount: response.applicationCount ?? undefined,
      mainContents: response.mainContents ?? [],
      isEnabledCommonContent: response.showCommonPage,
      createdAt: new Date(response.createdAt),
      updatedAt: new Date(response.updatedAt),
      internalUsers,
      externalUsers,
    }
  },

  toEntities: (response: ProjectsResponse): ProjectEntity[] => {
    return (
      response.projects?.map(project => {
        return {
          ...project,
          status: {
            ...project.status,
            priority: project.status.priority ?? undefined,
          },
          name: project.title,
          structureCount: project.structureCount ?? undefined,
          roomCount: project.roomCount ?? undefined,
          planCount: project.planCount ?? undefined,
          averagePriceRange: project.averagePriceRange ?? undefined,
          purchaserCount: project.purchaserCount ?? undefined,
          applicationCount: project.applicationCount ?? undefined,
          createdAt: new Date(project.createdAt),
          updatedAt: new Date(project.updatedAt),
        }
      }) ?? []
    )
  },
} as const
